import React, { useCallback, useRef } from 'react';
import { AddImageControlPanelContent } from './components/add-image-control-panel-content';
import { ControlPanelShell } from './components/control-panel/shell';
import { EditImageControlPanelContent } from './components/edit-image-control-panel-content';
import { useEditorStore } from './editor-store';
import { Block, NativeESignatureBlockContent, PlaceholderBlockContent, VideoBlockContent } from './types';
import { AddLinkControlPanelContent } from './links/add-link-control-panel-content';
import { EditLinkControlPanelContent } from './links/edit-link-control-panel-content';
import { LinkDisplayOption, LinkResponse } from './links/types';
import { Content } from 'src/common/interfaces/content.interface';
import { AddMediaControlPanelContent } from './media/add-media-control-panel-content';
import { useBlockActions } from './helpers/actions/use-block-actions.hook';
import { useImageBlockOperations } from './helpers/block/use-image-block-operations';
import { functionNoop } from 'src/utils/function/noop';
import { AddFileControlPanelContent } from './files/add-file-control-panel-content';
import { EditAttachmentControlPanelContent } from './files/edit-attachment-control-panel-content';
import { EditPdfControlPanelContent } from './files/edit-pdf-control-panel-content';
import { ACCEPTED_IMAGE_MIME_TYPES } from './helpers/file-to-block-content-type';
import { AddLogoControlPanelContent } from './logo/add-logo-control-panel-content';
import { EditVideoControlPanelContent } from './files/edit-video-control-panel-content';
import { isMicrosoftOfficeUrl } from 'src/utils/mime';
import { ContentsListControlPanelContent } from './contents-list/control-panel-content';
// import { TextControlPanelContent } from './components/text-control-panel-content';
import { Nullable } from 'src/types/nullable.type';
import { useEditorContext } from 'src/store/editor';
import { AddRecordingControlPanel } from './recording/control-panel';
import { AddEsignatureLinkControlPanel } from './links/add-esignature-link-control-panel-content';
import { IntegrationConnectWithIntegrationSection } from './media/connect-with-integration-section';
import { EditNativeESignatureControlPanel } from './native-e-signature/edit-control-panel';
import { AddNativeESignatureBlockControlPanel } from './native-e-signature/add-control-panel';
import { TableControlPanelContent } from './components/table/control-panel-content';
import { AddCtaButtonPanelContent } from './cta-button/panel';
import { ToggleVideoDocPersonalContent } from './components/toggle-video-doc-personal-content';
import { AddNativeFormPanel } from './native-form/add-panel';

type Props = {
  block: Block;
};

export function getBlockKey(block: Block) {
  return `${block.id}${block.content.type}`;
}

export const BlockControlPanel = ({ block }: Props) => {
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const {
    onNewUrl,
    onNewFile,
    onNewPdfContentFromLibrary,
    onNewAttachmentContentFromLibrary,
    onNewMsOfficeContentFromLibrary,
    onNewVideoContentFromLibrary,
    onNewVideoFromExternalUrl,
    onDeleteBlock,
    onBlockParamsChange,
    onNewNativeESignatureBlock,
  } = useBlockActions(block);
  const { onNewImageUrl, activeTab, setActiveTab } = useImageBlockOperations(block);
  const [replacingContent, setReplacingContent] = React.useState(false);
  const clearSelectedBlock = useEditorStore((state) => state.clearSelectedBlock);
  const selectedBlockId = useEditorStore((state) => state.selectedBlockId);
  const onControlPanelDragStopRef = useRef(functionNoop);
  const { journey } = useEditorContext();
  const onReplacingFile = useCallback(
    (file: File) => {
      onNewFile(file);
      setReplacingContent(false);
    },
    [onNewFile]
  );

  const renderControlPanel = (title: string, subtitle: Nullable<string>, content: React.ReactNode) => {
    const isPlaceholderAddMedia = block.content.type === 'placeholder' && block.content.placeholderType === 'add-media';
    // use larger size if it's placeholder block and add-media
    const size = isPlaceholderAddMedia ? 'large' : 'regular';

    const onDragStop = () => {
      if (isPlaceholderAddMedia) {
        onControlPanelDragStopRef.current();
      }
    };

    return (
      <ControlPanelShell
        onDragStop={onDragStop}
        title={title}
        subtitle={subtitle || undefined}
        key={getBlockKey(block)}
        size={size}
        active={block.id === selectedBlockId}
      >
        {content}
      </ControlPanelShell>
    );
  };

  const setBlockToLoading = (personalContent = true) => {
    onBlockParamsChange(block.id, { fileStatus: 'in-progress', personalContent }, { undoable: false, syncable: false });
    clearSelectedBlock();
  };

  const resetBlockLoading = () => {
    onBlockParamsChange(
      block.id,
      { fileStatus: undefined, personalContent: true },
      { undoable: false, syncable: false }
    );
  };

  const onSelectExistingContent = async (content: Content) => {
    const { uuid, embed, external_link, image_asset, file_asset, video_asset } = content;

    if (embed || external_link) {
      let url = '';
      if (embed) {
        url = embed.transformed_url;
      } else if (external_link) {
        url = external_link.url;
      }

      if (url) {
        setBlockToLoading();
        const isLinkBox = url.includes('app.box');
        await onNewUrl(url, content.name, undefined, false, isLinkBox);
        resetBlockLoading();
      }
    } else if (image_asset) {
      setBlockToLoading();
      if (ACCEPTED_IMAGE_MIME_TYPES.includes(image_asset.content_type)) {
        const { url, dalle_params, name } = image_asset;
        await onNewImageUrl({
          url,
          dalleParams: dalle_params,
          name,
          contentUUID: uuid,
        });
      } else {
        onNewAttachmentContentFromLibrary(image_asset, uuid);
      }
      resetBlockLoading();
    } else if (video_asset) {
      setBlockToLoading(false);
      onNewVideoContentFromLibrary(content);
      resetBlockLoading();
    } else if (file_asset) {
      setBlockToLoading();
      if (file_asset.content_type.endsWith('pdf')) {
        onNewPdfContentFromLibrary(content, resetBlockLoading);
      } else if (isMicrosoftOfficeUrl(file_asset.url)) {
        onNewMsOfficeContentFromLibrary(file_asset, uuid, resetBlockLoading);
      } else {
        onNewAttachmentContentFromLibrary(file_asset, uuid, resetBlockLoading);
      }
    }
  };

  if (block.content.type === 'table') {
    return renderControlPanel('Edit table', null, <TableControlPanelContent block={block} />);
  } else if (block.content.type === 'cta-button') {
    return renderControlPanel(
      'Add CTA button',
      null,
      <AddCtaButtonPanelContent
        block={block}
        onNewUrl={onNewUrl}
        onTextChange={(text) => {
          onBlockParamsChange(block.id, { text });
        }}
        onColorsChange={(colors) => {
          onBlockParamsChange(block.id, colors);
        }}
      />
    );
  } else if (block.content.type === 'native-form') {
    return renderControlPanel('Add form', null, <AddNativeFormPanel block={block} />);
  } else if (block.content.type === 'placeholder') {
    if (block.content.integration) {
      const { type } = block.content.integration;
      const connectWithIntegrationSection = (
        <IntegrationConnectWithIntegrationSection
          integrationType={type}
          onNewUrl={onNewUrl}
          onNewVideoFromExternalUrl={onNewVideoFromExternalUrl}
          setBlockToLoading={setBlockToLoading}
        />
      );

      return renderControlPanel(
        block.content.integration.label,
        'Make sure your link has the relevant permissions',
        <>
          <AddLinkControlPanelContent onNewUrl={onNewUrl} isIntegration integrationType={type} />
          {connectWithIntegrationSection}
        </>
      );
    }
    if (block.content.placeholderType === 'add-image') {
      return renderControlPanel(
        'Add Image',
        activeTab === 'ai' ? 'Enter a prompt and let AI do the work' : 'Upload an image',
        <AddImageControlPanelContent
          key={`add-image-control-panel-{block.id}`}
          block={block}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onNewFile={onNewFile}
        />
      );
    } else if (block.content.placeholderType === 'add-link') {
      return renderControlPanel('Add link', null, <AddLinkControlPanelContent onNewUrl={onNewUrl} />);
    } else if (block.content.placeholderType === 'add-media') {
      return renderControlPanel(
        'Library',
        null,
        <AddMediaControlPanelContent
          onControlPanelDragEndRef={onControlPanelDragStopRef}
          onSelectContent={onSelectExistingContent}
          onNewUrl={(url: string, title: string, isBoxLink) => {
            setBlockToLoading();
            onNewUrl(url, title, undefined, false, isBoxLink);
          }}
          onNewVideo={onNewVideoFromExternalUrl}
          setBlockToLoading={setBlockToLoading}
        />
      );
    } else if (block.content.placeholderType === 'add-file') {
      const { fileType, clipboardData } = block.content as PlaceholderBlockContent;
      let title = 'Add file';
      let description = 'Upload videos, images, PDFs, attachments or other files into this block';
      if (fileType?.type === 'video') {
        title = 'Add video';
        description = 'Upload videos into this block';
      } else if (fileType?.type === 'document') {
        title = 'Add document';
        description = 'Upload PDF, DOC or DOCX documents into this block';
      }
      return renderControlPanel(title, description, <AddFileControlPanelContent onNewFile={onNewFile} />);
    } else if (block.content.placeholderType === 'add-logo') {
      return renderControlPanel('Edit logo', null, <AddLogoControlPanelContent block={block} />);
    } else if (block.content.placeholderType === 'add-recording') {
      return <AddRecordingControlPanel render={renderControlPanel} block={block} onDeleteBlock={onDeleteBlock} />;
    } else if (block.content.placeholderType === 'native-form') {
      return renderControlPanel('Add form', null, <AddNativeFormPanel block={block} />);
    } else if (block.content.placeholderType === 'add-esignature-link') {
      return renderControlPanel(
        'e-Signature',
        'Embed your document below. Make sure it has the relevant permissions',
        <AddEsignatureLinkControlPanel onNewUrl={onNewUrl} />
      );
    } else if (block.content.placeholderType === 'add-native-e-signature') {
      return renderControlPanel(
        'e-Signature',
        'Who are you sending this Journey to? We use these details to create the block and no emails are sent.',
        <AddNativeESignatureBlockControlPanel
          onRecipientAdded={(recipient) => {
            onNewNativeESignatureBlock({ signees: [recipient] });
          }}
        />
      );
    }
  } else if (block.content.type === 'image') {
    if (replacingContent) {
      return renderControlPanel('Add file', null, <AddFileControlPanelContent onNewFile={onReplacingFile} />);
    }
    return renderControlPanel(
      block.content.dalleParams?.prompt ? 'AI Image' : 'Edit image',
      block.content.dalleParams?.prompt ? 'Enter a prompt and let AI do the work' : null,
      <EditImageControlPanelContent
        block={block}
        onChangeObjectFit={(objectFit) => {
          onBlockParamsChange(block.id, { objectFit, personalContent: true });
        }}
        onReplaceBlock={() => {
          setReplacingContent(true);
        }}
      />
    );
  } else if (block.content.type === 'link') {
    return renderControlPanel(
      'Edit link',
      null,
      <EditLinkControlPanelContent
        block={block}
        onShowImageChange={(checked: boolean) => {
          if (block.content.type !== 'link') return;
          onBlockParamsChange(block.id, { hideImage: !checked, personalContent: true });
        }}
        onOpenInNewTabChange={(openInNewTab: boolean) => {
          onBlockParamsChange(block.id, { openInNewTab, personalContent: true });
        }}
        onDisplayOptionChange={(link: LinkResponse, displayOption: LinkDisplayOption) => {
          if (block.content.type !== 'link') return;

          dispatchUserEditorAction(
            {
              type: 'replace-block',
              id: block.id,
              content: {
                type: 'link',
                link,
                displayOption,
              },
              layoutExtraParams: {},
            },
            {
              syncable: true,
              undoable: true,
            }
          );
        }}
        onNewUrl={onNewUrl}
      />
    );
  } else if (block.content.type === 'pdf') {
    if (replacingContent) {
      return renderControlPanel('Add file', null, <AddFileControlPanelContent onNewFile={onReplacingFile} />);
    }
    return renderControlPanel(
      'Edit file',
      null,
      <EditPdfControlPanelContent
        block={block}
        onReplaceBlock={() => {
          setReplacingContent(true);
        }}
        onAllowDownloadChange={(checked: boolean) => {
          if (block.content.type !== 'pdf') return;
          onBlockParamsChange(block.id, { allowDownload: checked });
        }}
      />
    );
  } else if (block.content.type === 'attachment') {
    if (replacingContent) {
      return renderControlPanel('Add file', null, <AddFileControlPanelContent onNewFile={onReplacingFile} />);
    }
    return renderControlPanel(
      'Edit file',
      null,
      <EditAttachmentControlPanelContent
        block={block}
        onReplaceBlock={() => {
          setReplacingContent(true);
        }}
      />
    );
  } else if (block.content.type === 'contents-list') {
    return renderControlPanel(
      'Edit contents',
      null,
      <ContentsListControlPanelContent
        block={block}
        onAlignmentChange={(alignment) => {
          onBlockParamsChange(block.id, { alignment });
        }}
        onToggleIndex={(checked) => {
          onBlockParamsChange(block.id, { enableIndex: checked });
        }}
      />
    );
  } else if (block.content.type === 'logo') {
    return renderControlPanel('Edit logo', null, <AddLogoControlPanelContent block={block} />);
  } else if (block.content.type === 'video') {
    const blockContent = block.content as VideoBlockContent;
    const hasTranscript = blockContent?.hasTranscript || false;
    const createdAt = blockContent?.jnyContent?.created_at || null;

    // check if the video is created more than 24 hours ago
    const isOldVideo = createdAt ? new Date(createdAt) < new Date(Date.now() - 24 * 60 * 60 * 1000) : false;

    if (!hasTranscript && isOldVideo) {
      // if the video is old and there is still no transcript, then dont show the control panel
      return null;
    }

    if (!hasTranscript) {
      return renderControlPanel(
        'Add transcript',
        'A transcript is being generated for this video. This might take a few minutes.',
        <ToggleVideoDocPersonalContent
          block={block}
          contentObject={{
            file_url: blockContent.url || blockContent?.jnyContent?.video_asset?.url || '',
            file_content_type: blockContent.file?.type || '',
            name:
              blockContent?.file?.name ||
              blockContent?.metadata?.name ||
              blockContent?.jnyContent?.video_asset?.url ||
              '',
          }}
        />
      );
    }

    return renderControlPanel('Create content from video', null, <EditVideoControlPanelContent block={block} />);
  } else if (block.content.type === 'native-e-signature') {
    return (
      <EditNativeESignatureControlPanel
        journey={journey}
        block={block}
        onHighlightedChange={(highlighted) => onBlockParamsChange(block.id, { highlighted })}
        onPinnedChange={(pinned) => onBlockParamsChange(block.id, { pinned })}
        onSignatureRecordChange={(signatureRecord) => {
          onBlockParamsChange(block.id, { signature_record: signatureRecord });
        }}
        onSigneesChange={(signees) => {
          onBlockParamsChange(block.id, { signees });
        }}
      />
    );
  }

  return null;
};
